// Global Instructions Rule Applied!
//
// useRefreshOnActivate — local standard for keeping a kept-mounted surface
// fresh (owner decision 2026-08-26; see docs/FRONTEND_DESIGN_PATTERNS.md).
//
// The app shell never unmounts tabs (TabContentWrapper hides them with
// display:none), and the record forms' inner AntD tabs stay mounted after
// first activation, so a mount-time-only fetch goes stale the moment the
// same record is touched from another tab (the "new record invisible until
// hard refresh" family, owner report 2026-08-26). This hook revalidates a
// surface when the user's attention returns to it — the same heuristic
// mainstream server-cache libraries ship as refetchOnWindowFocus — while
// continuing to render the current data (no spinner, no flicker).
//
// Mechanics: attach the returned ref to the surface's root element. An
// IntersectionObserver fires when the element transitions hidden → visible
// (both display:none tab switches and inner-tab revisits), and the refetch
// runs when the data is older than maxAgeMs. No tab-key plumbing needed.
//
// This complements (does not replace) the onRefreshReady handshake — the
// form Refresh buttons stay the explicit path, this is the automatic one.
import { useCallback, useEffect, useRef } from 'react'

interface RefreshOnActivateOptions {
  /** Refetch only when the data is older than this (ms). 0 = always. */
  maxAgeMs?: number
}

export function useRefreshOnActivate(
  refetch: () => Promise<void> | void,
  { maxAgeMs = 30_000 }: RefreshOnActivateOptions = {}
): (node: HTMLElement | null) => void {
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch
  const maxAgeRef = useRef(maxAgeMs)
  maxAgeRef.current = maxAgeMs
  // The surface is fresh at mount — its own mount effect fetches.
  const lastFreshRef = useRef(Date.now())
  const wasHiddenRef = useRef(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const attach = useCallback((node: HTMLElement | null): void => {
    observerRef.current?.disconnect()
    observerRef.current = null
    if (!node || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.some((e) => e.isIntersecting)
      if (!visible) {
        wasHiddenRef.current = true
        return
      }
      if (wasHiddenRef.current && Date.now() - lastFreshRef.current > maxAgeRef.current) {
        lastFreshRef.current = Date.now()
        void refetchRef.current()
      }
      wasHiddenRef.current = false
    })
    observer.observe(node)
    observerRef.current = observer
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  return attach
}

export default useRefreshOnActivate
